import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Ban, BookOpen, Pencil, SquarePen } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import VoidConfirmationModal from '../components/VoidConfirmationModal';
import StartingValueCostCorrectionModal from '../components/inventory/StartingValueCostCorrectionModal';
import {
  correctItemStartingValue,
  getItemStartingValue,
  previewItemStartingValueCorrection,
  voidItemStartingValue,
} from '../utils/inventoryStartingValueApi';
import { getItemById } from '../utils/itemApi';
import { ApiValidationError } from '../types/auth';
import type {
  InventoryStartingValue,
  StartingValueCorrectionPreview,
} from '../types/inventoryStartingValue';
import type { Item } from '../types/item';
import { formatCurrency, formatPreviewQuantity } from '../utils/formatCurrency';
import { toast } from '../utils/toast';
import { startingValueVoidCopy } from '../utils/voidConfirmationCopy';
import {
  canSetStartingValue,
  canShowStartingValueCostCorrection,
  canShowStartingValueEdit,
  canVoidStartingValue,
  formatStartingValueDate,
  formatStartingValueLabel,
  formatStartingValueTime,
  getStartingValueStatusColor,
} from '../utils/inventoryStartingValueForm';

const InventoryStartingValueView = () => {
  const { itemId: itemIdParam } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const itemId = Number(itemIdParam);

  const [item, setItem] = useState<Item | null>(null);
  const [startingValue, setStartingValue] = useState<InventoryStartingValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notSet, setNotSet] = useState(false);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionPreview, setCorrectionPreview] = useState<StartingValueCorrectionPreview | null>(
    null
  );
  const [pendingUnitCost, setPendingUnitCost] = useState<number | null>(null);
  const [isPreviewingCorrection, setIsPreviewingCorrection] = useState(false);
  const [isConfirmingCorrection, setIsConfirmingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);

  const fetchDetail = async () => {
    if (!itemIdParam || Number.isNaN(itemId)) {
      setError('Invalid item ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setNotSet(false);

      const [itemData, sv] = await Promise.all([
        getItemById(itemId),
        getItemStartingValue(itemId),
      ]);

      setItem(itemData);
      if (!sv) {
        setNotSet(true);
        setStartingValue(null);
      } else {
        setStartingValue(sv);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load opening stock');
      setItem(null);
      setStartingValue(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [itemIdParam]);

  const handleViewJournal = () => {
    if (startingValue?.transaction_id == null) return;
    navigate('/accounts', {
      state: {
        activeTab: 'journal',
        referenceTransactionId: startingValue.transaction_id,
      },
    });
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async (reason: string) => {
    if (!canVoidStartingValue() || !startingValue) return;

    try {
      setVoiding(true);
      setVoidError(null);
      const voided = await voidItemStartingValue(itemId, {
        void_reason: reason || null,
      });
      setShowVoidModal(false);
      setStartingValue(voided);
      toast.success(`Opening stock voided for ${voided.item.name}.`);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setVoidError(err.message);
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to void opening stock');
      }
    } finally {
      setVoiding(false);
    }
  };

  const openCorrectionModal = () => {
    setCorrectionError(null);
    setCorrectionPreview(null);
    setPendingUnitCost(null);
    setShowCorrectionModal(true);
  };

  const cancelCorrectionModal = () => {
    setShowCorrectionModal(false);
    setCorrectionError(null);
    setCorrectionPreview(null);
    setPendingUnitCost(null);
  };

  const backFromCorrectionPreview = () => {
    setCorrectionPreview(null);
    setPendingUnitCost(null);
    setCorrectionError(null);
  };

  const handleCorrectionPreview = async (unitCost: number) => {
    if (!canShowStartingValueCostCorrection(startingValue)) return;

    try {
      setIsPreviewingCorrection(true);
      setCorrectionError(null);
      const preview = await previewItemStartingValueCorrection(itemId, { unit_cost: unitCost });
      setCorrectionPreview(preview);
      setPendingUnitCost(unitCost);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setCorrectionError(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : 'Failed to preview opening stock correction'
        );
      }
    } finally {
      setIsPreviewingCorrection(false);
    }
  };

  const confirmCorrection = async () => {
    if (!canShowStartingValueCostCorrection(startingValue) || pendingUnitCost == null) return;

    try {
      setIsConfirmingCorrection(true);
      setCorrectionError(null);
      const result = await correctItemStartingValue(itemId, { unit_cost: pendingUnitCost });
      setShowCorrectionModal(false);
      setCorrectionPreview(null);
      setPendingUnitCost(null);
      setStartingValue(result.data);
      toast.success(
        `Opening unit cost corrected for ${result.data.item.name}. Inventory, equity, and COGS were updated.`
      );
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setCorrectionError(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : 'Failed to correct opening stock unit cost'
        );
      }
    } finally {
      setIsConfirmingCorrection(false);
    }
  };

  const canVoidDetail =
    startingValue != null && startingValue.status !== 'VOIDED' && canVoidStartingValue();
  const canCorrectDetail = canShowStartingValueCostCorrection(startingValue);
  const canEditDetail = canShowStartingValueEdit(startingValue);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-2">Could not load opening stock</p>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={fetchDetail}
                className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
              >
                Retry
              </button>
              <Link
                to="/inventory"
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Back to Inventory
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (notSet && item) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          <Link
            to="/inventory"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Inventory
          </Link>
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-900 font-medium mb-2">No opening stock set</p>
            <p className="text-sm text-gray-500 mb-1">
              {item.name} ({item.sku})
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Opening stock has not been recorded for this item.
            </p>
            {canSetStartingValue() && (
              <Link
                to={`/manage-inventory-starting-values?itemId=${item.id}`}
                className="inline-flex px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
              >
                Set opening stock
              </Link>
            )}
          </div>
        </main>
      </div>
    );
  }

  if (!startingValue || !item) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-hidden">
        <div className="w-full max-w-full">
          <div className="mb-6">
            <Link
              to="/inventory"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Inventory
            </Link>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                  Opening Stock — {startingValue.item.name}
                </h1>
                <p className="text-sm text-gray-500 font-mono mt-1">{startingValue.item.sku}</p>
                <div className="flex gap-2 mt-2">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStartingValueStatusColor(startingValue.status)}`}
                  >
                    {formatStartingValueLabel(startingValue.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm mb-6 max-w-xl">
              <h2 className="font-semibold text-gray-900 mb-3">Opening details</h2>
              <div className="flex justify-between">
                <span className="text-gray-600">As-of date</span>
                <span>{formatStartingValueDate(startingValue.as_of_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Quantity</span>
                <span className="font-mono">{formatPreviewQuantity(startingValue.qty)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Unit cost</span>
                <span className="font-mono">{formatCurrency(startingValue.unit_cost)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 font-semibold">
                <span>Total value</span>
                <span className="font-mono">{formatCurrency(startingValue.total_value)}</span>
              </div>
              {startingValue.equity_account && (
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Equity account</span>
                  <span>
                    {startingValue.equity_account.account_number} —{' '}
                    {startingValue.equity_account.name}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Memo</span>
                <span className="text-right max-w-[60%]">{startingValue.memo || '—'}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Created</span>
                <span>
                  {formatStartingValueDate(startingValue.created_at)}{' '}
                  {formatStartingValueTime(startingValue.created_at)}
                </span>
              </div>
              {startingValue.status === 'VOIDED' && startingValue.void_reason && (
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Void reason</span>
                  <span className="text-right max-w-[60%]">{startingValue.void_reason}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              <Link
                to="/inventory?tab=stock-valuation"
                className="px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Stock valuation report
              </Link>
              {startingValue.transaction_id != null && (
                <button
                  type="button"
                  onClick={handleViewJournal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50"
                >
                  <BookOpen className="h-4 w-4" />
                  View journal
                </button>
              )}
              {canEditDetail && (
                <Link
                  to={`/manage-inventory-starting-values?itemId=${startingValue.item_id}&mode=edit`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <SquarePen className="h-4 w-4" />
                  Edit
                </Link>
              )}
              {canCorrectDetail && (
                <button
                  type="button"
                  onClick={openCorrectionModal}
                  disabled={isPreviewingCorrection || isConfirmingCorrection}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-amber-800 border border-amber-200 rounded-lg hover:bg-amber-50"
                >
                  <Pencil className="h-4 w-4" />
                  Correct cost
                </button>
              )}
              {canVoidDetail && (
                <button
                  type="button"
                  onClick={openVoidModal}
                  disabled={voiding}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  <Ban className="h-4 w-4" />
                  Void
                </button>
              )}
            </div>
          </div>
        </div>

        {startingValue && (
          <VoidConfirmationModal
            isOpen={showVoidModal}
            {...startingValueVoidCopy(startingValue.item.name)}
            confirmText="Void opening stock"
            showReasonField
            isSubmitting={voiding}
            error={voidError}
            onCancel={cancelVoidModal}
            onConfirm={confirmVoid}
          />
        )}

        {startingValue && (
          <StartingValueCostCorrectionModal
            isOpen={showCorrectionModal}
            itemName={startingValue.item.name}
            currentUnitCost={startingValue.unit_cost}
            isPreviewing={isPreviewingCorrection}
            isConfirming={isConfirmingCorrection}
            preview={correctionPreview}
            error={correctionError}
            onPreview={handleCorrectionPreview}
            onConfirm={confirmCorrection}
            onBack={backFromCorrectionPreview}
            onCancel={cancelCorrectionModal}
          />
        )}
      </main>
    </div>
  );
};

export default InventoryStartingValueView;
